import * as forms from "@client/forms";
import {AppDispatch, GetState, useAppDispatch, useAppSelector} from "@client/store";
import * as utils from "@client/utils";
import {models, server} from "@reactivated";
import {Decimal} from "decimal.js";
import {produce} from "immer";
import React from "react";

let isLoading = false;

// The journal calls this when it shows a workout, so opening one does not fetch
// the catalog in the middle of its slide.
export const loadWorkoutData =
    () => async (dispatch: AppDispatch, getState: GetState) => {
        if (getState().search.workouts.data != null || isLoading) {
            return;
        }
        isLoading = true;
        const result = await server.journal.workouts.workouts();

        isLoading = false;
        if (result.type == "success") {
            dispatch({type: "SET_WORKOUTS", workouts: result.data});
        }
    };

export const useWorkoutData = () => {
    const dispatch = useAppDispatch();
    const workouts = useAppSelector((state) => state.search.workouts.data);

    React.useEffect(() => {
        void dispatch(loadWorkoutData());
    }, []);

    return workouts;
};

export function annotateWithExercises<
    T extends
        | server.journal.workouts.Workout.output
        | server.journal.workouts.WorkoutTemplate.output
        | server.journal.workouts.WorkoutSection.output,
>(template: T) {
    if ("groups" in template) {
        return {
            ...template,
            exercises: template.groups.map((g) => g.exercises).flat(),
        };
    }
    return {
        ...template,
        exercises: template.sections
            .map((s) => s.groups)
            .flat()
            .map((g) => g.exercises)
            .flat(),
    };
}

function getSuggestedWeight({
    max,
    set,
    isImperial,
}: {
    max: string | null;
    set: server.journal.workouts.ExerciseSet.output;
    isImperial: boolean;
}) {
    const smallest_plate = new Decimal(isImperial ? "2.5" : "1");

    if (set.weight_percentage == null || max == null) {
        return null;
    }

    const percentage = new Decimal(set.weight_percentage).dividedBy(100);

    return utils
        .roundNumberToNearest(
            new Decimal(max)
                .times(percentage)
                .dividedBy(isImperial ? "0.45359237" : "1"),
            smallest_plate.times(2),
        )
        .toString();
}

function useWorkoutSource(
    workout: Pick<server.journal.workouts.Workout.output, "sections" | "questions">,
    setWorkout?: (
        value: (
            prev: server.journal.workouts.Workout.output,
        ) => server.journal.workouts.Workout.output,
    ) => void,
) {
    const [sections, setSections] = React.useState(workout.sections);
    const [questions, setQuestions] = React.useState(workout.questions);

    if (setWorkout != null) {
        return {
            sections: workout.sections,
            questions: workout.questions,
            setQuestions: (questions: server.journal.workouts.Question.output[]) => {
                setWorkout((prev) => ({
                    ...prev,
                    questions: questions,
                }));
            },
            setSections: (
                value: (
                    prevState: server.journal.workouts.WorkoutSection.output[],
                ) => server.journal.workouts.WorkoutSection.output[],
            ) => {
                setWorkout((prev) => ({
                    ...prev,
                    sections: value(prev.sections),
                }));
            },
        };
    }

    return {
        sections,
        setSections,
        questions,
        setQuestions,
    };
}

export const useWorkout = ({
    workout,
    setWorkout,
    exercise_maxes: maxes,
}: {
    workout: Pick<server.journal.workouts.Workout.output, "sections" | "questions">;
    setWorkout?: (
        value: (
            prev: server.journal.workouts.Workout.output,
        ) => server.journal.workouts.Workout.output,
    ) => void;
    exercise_maxes?: models["journal.workouts.ExerciseMaxes"];
}) => {
    const isImperial = forms.useIsImperial();

    const {
        sections: _sections,
        setSections,
        questions,
        setQuestions,
    } = useWorkoutSource(workout, setWorkout);

    const sections = React.useMemo(() => {
        return _sections.map((section, sectionIndex) => ({
            ...section,
            groups: section.groups.map((group, groupIndex) => ({
                ...group,
                exercises: group.exercises.map((exercise, exerciseIndex) => ({
                    ...exercise,
                    sets: exercise.sets.map((set, setIndex) => ({
                        ...set,
                        suggested_weight: getSuggestedWeight({
                            set,
                            isImperial,
                            max:
                                maxes?.actual.find(
                                    (max) =>
                                        max.exercise_type.uuid == exercise.type.uuid,
                                )?.weight ?? null,
                        }),
                        __update: (
                            set: Partial<server.journal.workouts.ExerciseSet.output>,
                        ) => {
                            setSections((_sections) =>
                                produce(_sections, (draft) => {
                                    draft[sectionIndex].groups[groupIndex].exercises[
                                        exerciseIndex
                                    ].sets[setIndex] = {
                                        ...draft[sectionIndex].groups[groupIndex]
                                            .exercises[exerciseIndex].sets[setIndex],
                                        ...set,
                                    };
                                }),
                            );
                        },
                    })),
                    __update: (
                        exercise: Partial<server.journal.workouts.Exercise.output>,
                    ) => {
                        setSections((_sections) =>
                            produce(_sections, (draft) => {
                                draft[sectionIndex].groups[groupIndex].exercises[
                                    exerciseIndex
                                ] = {
                                    ...draft[sectionIndex].groups[groupIndex].exercises[
                                        exerciseIndex
                                    ],
                                    ...exercise,
                                };
                            }),
                        );
                    },
                    __delete: () => {
                        setSections((_sections) =>
                            produce(_sections, (draft) => {
                                draft[sectionIndex].groups[groupIndex].exercises.splice(
                                    exerciseIndex,
                                    1,
                                );
                            }),
                        );
                    },
                })),
                __update: (
                    group: Partial<server.journal.workouts.ExerciseGroup.output>,
                ) => {
                    setSections((_sections) =>
                        produce(_sections, (draft) => {
                            draft[sectionIndex].groups[groupIndex] = {
                                ...draft[sectionIndex].groups[groupIndex],
                                ...group,
                            };
                        }),
                    );
                },
                __delete: () => {
                    setSections((_sections) =>
                        produce(_sections, (draft) => {
                            draft[sectionIndex].groups.splice(groupIndex, 1);
                        }),
                    );
                },
            })),
            __update: (
                section: Partial<server.journal.workouts.WorkoutSection.output>,
            ) => {
                setSections((_sections) =>
                    produce(_sections, (draft) => {
                        draft[sectionIndex] = {
                            ...draft[sectionIndex],
                            ...section,
                        };
                    }),
                );
            },
        }));
    }, [_sections, maxes]);

    const exercises = sections
        .map((s) => s.groups)
        .flat()
        .map((g) => g.exercises)
        .flat();

    const exerciseTypes = exercises.map((e) => e.type);

    const reset = (
        workout: Pick<server.journal.workouts.Workout.output, "sections" | "questions">,
    ) => {
        setSections(() => workout.sections);
        setQuestions(workout.questions);
    };
    return {
        exercises,
        exerciseTypes,
        sections,
        setSections,
        questions,
        setQuestions,
        reset,
    };
};

export type WorkoutSection = ReturnType<typeof useWorkout>["sections"][number];

export type ExerciseGroup = WorkoutSection["groups"][number];

export type Exercise = ExerciseGroup["exercises"][number];

export type ExerciseSet = Exercise["sets"][number];

type Category = server.journal.workouts.ExerciseType.output["category"];
export type CardioCategory = "CARDIO";
export type StrengthCategory = Exclude<Category, CardioCategory>;

// A narrowed Exercise whose type.category is constrained to a subset, so the
// per-variant editors accept only the categories they actually render. The
// dispatch narrows into these via ts-pattern (plain control flow can't, since
// the discriminant is nested under `type`).
export type ExerciseWithCategory<C extends Category> = Omit<Exercise, "type"> & {
    type: Omit<Exercise["type"], "category"> & {category: C};
};
export type CardioExercise = ExerciseWithCategory<CardioCategory>;
export type StrengthExercise = ExerciseWithCategory<StrengthCategory>;

export type Workout = ReturnType<typeof useWorkout>;

export const WorkoutContext = React.createContext<{
    workout: server.journal.workouts.Workout.output;
    scope: "consumer" | "client" | "manager";
    startRestTimer: (duration: number | null) => void;
    // Waits for the editor's queued save, so an action that needs the workout
    // on the server (a recording) runs against what is there.
    flush: () => Promise<unknown>;
} | null>(null);

export const ExerciseContext = React.createContext<{
    exercise: server.journal.workouts.Exercise.output;
} | null>(null);

type SetOutput = server.journal.workouts.ExerciseSet.output;

export const getDefaultDistanceType = (category: Category, isImperial: boolean) => {
    switch (category) {
        case "REPS_DISTANCE":
        case "WEIGHT_DISTANCE":
        case "WEIGHT_DURATION_DISTANCE":
        case "DURATION_DISTANCE":
            return isImperial ? "FEET" : "METERS";
        case "CARDIO":
            return isImperial ? "MILES" : "KILOMETERS";
        case "WEIGHT":
        case "BODYWEIGHT":
        case "DURATION":
        case "MEDLEY":
            return null;
    }
    const _exhaustiveCheck: never = category;
    return _exhaustiveCheck;
};

export const getDefaultWeightType = (category: Category) => {
    switch (category) {
        case "WEIGHT":
        case "BODYWEIGHT":
        case "WEIGHT_DISTANCE":
        case "WEIGHT_DURATION_DISTANCE":
            return "VALUE";
        case "DURATION_DISTANCE":
        case "CARDIO":
        case "REPS_DISTANCE":
        case "DURATION":
        case "MEDLEY":
            return null;
    }
    const _exhaustiveCheck: never = category;
    return _exhaustiveCheck;
};

// New uuids, side kept, and each drop pointed at its parent's copy.
export function copySets<S extends SetOutput>(sets: S[]): S[] {
    const copies = new Map(sets.map((set) => [set.uuid, crypto.randomUUID()]));
    return sets.map((set) => {
        const parent = set.drop_of == null ? undefined : copies.get(set.drop_of.uuid);
        return {
            ...set,
            uuid: copies.get(set.uuid)!,
            drop_of: parent == null ? null : {uuid: parent},
        };
    });
}

// Sides apply to strength work alone: never to cardio or a medley.
const isSided = (
    exerciseType: Pick<
        server.journal.workouts.ExerciseType.output,
        "category" | "unilateral"
    >,
) =>
    exerciseType.unilateral &&
    exerciseType.category != "CARDIO" &&
    exerciseType.category != "MEDLEY";

// A new exercise with no history: one blank set, or an L/R pair when unilateral.
export function blankSets(
    exerciseType: Pick<
        server.journal.workouts.ExerciseType.output,
        "category" | "description" | "unilateral"
    >,
    isImperial: boolean,
): SetOutput[] {
    const blank = (side: SetOutput["side"]): SetOutput => ({
        uuid: crypto.randomUUID(),
        type: "WORKING",
        instructions: exerciseType.category == "MEDLEY" ? exerciseType.description : "",
        distance_type: getDefaultDistanceType(exerciseType.category, isImperial),
        duration_type: exerciseType.category == "DURATION" ? "SECONDS" : null,
        speed_type: null,
        duration: null,
        progression: null,
        distance: null,
        speed: null,
        incline: null,
        resistance: null,
        reps: null,
        rpe: null,
        weight: null,
        status: "INCOMPLETE",
        attributes: [],
        side,
        drop_of: null,
        weight_type: getDefaultWeightType(exerciseType.category),
        weight_percentage: null,
    });
    return isSided(exerciseType) ? [blank("LEFT"), blank("RIGHT")] : [blank(null)];
}

// "Add set": a copy of the last set, or of the last L and last R when the
// exercise is unilateral. Never a drop.
export function nextSets(
    exercise: Pick<server.journal.workouts.Exercise.output, "type" | "sets">,
    isImperial: boolean,
): SetOutput[] {
    const {sets} = exercise;
    const last = (side: SetOutput["side"]) =>
        [...sets].reverse().find((set) => set.side == side);
    const previous = sets.at(-1);

    if (previous == null) {
        return blankSets(exercise.type, isImperial);
    }

    const copy = (source: SetOutput, side: SetOutput["side"]): SetOutput => ({
        ...source,
        uuid: crypto.randomUUID(),
        progression: null,
        status: "INCOMPLETE",
        side,
        drop_of: null,
    });

    if (!isSided(exercise.type)) {
        return [copy(previous, null)];
    }

    return [
        copy(last("LEFT") ?? previous, "LEFT"),
        copy(last("RIGHT") ?? previous, "RIGHT"),
    ];
}
